<template>
  <div class="container-fluid">
    <!-- Permission denied banner -->
    <div v-if="!skillAccess.canAccess && props.targetUserInfo" class="alert alert-warning d-flex align-items-center gap-2 mb-3">
      <i class="material-icons">lock</i>
      <span>{{ skillAccess.reason }}</span>
    </div>

    <!-- Preferred Activities -->
    <TagSelector optionName="activities" availableOptionsHeading="All Activities"
      selectedOptionsHeading="User's Preferred Activities" :initial-skills="filteredAvailableActivities"
      v-model:interests="userActivities" :on-save="skillAccess.canAccess ? handleSaveActivities : null" :loading="loading"
      :isSaving="activitiesSubmitting" :disabled="!skillAccess.canAccess" />

    <!-- Preferred Workgroups -->
    <div class="mt-4">
      <TagSelector optionName="workgroups" availableOptionsHeading="Preferred Activities Workgroups"
        selectedOptionsHeading="User's Preferred Activity Workgroups (user schedules will ONLY be generated for selected workgroups)"
        :initial-skills="filteredAvailableWorkgroups" v-model:interests="userWorkgroups" :on-save="skillAccess.canAccess ? handleSaveWorkgroups : null"
        :loading="loading" :isSaving="workgroupsSubmitting" :disabled="!skillAccess.canAccess" />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed, inject } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import TagSelector from "@/components/common/TagSelector.vue";
import { getLocationId, getLogginedPersonId } from "@/utils/SessionDataHandler";
import { usePermissions } from "@/composables/usePermissions";

const props = defineProps({
  targetUserInfo: { type: Object, default: null },
  groupAdminEdit: { type: Boolean, default: false },
});

const loading = ref(false);
const activitiesSubmitting = ref(false);
const workgroupsSubmitting = ref(false);

const store = useStore();
const route = useRoute();
const toast = useToast();
const injectedPeopleGroupId = inject('editUserPeopleGroupId', null);
const getPeopleGroupId = () => {
  const value = injectedPeopleGroupId?.value ?? null;
  return value ? Number(value) : null;
};
const {
  canAccessUserBySkill,
  isLocationAdmin,
  adminActivityIds,
  adminWorkgroupIds,
} = usePermissions();

const skillAccess = computed(() => {
  if (props.groupAdminEdit) return { canAccess: true, reason: "" };
  if (!props.targetUserInfo) return { canAccess: true, reason: "" };
  return canAccessUserBySkill(props.targetUserInfo);
});

// Get personId from inject (modal), route params, or session data
const _injectedPersonId = inject('editUserPersonId', null);
const getPersonId = () => {
  if (_injectedPersonId?.value) {
    return Number(_injectedPersonId.value);
  }
  if (route.params.userid) {
    return Number(route.params.userid);
  }
  // Fallback to session data if route param is not available
  const sessionPersonId = getLogginedPersonId() || store.getters.stSessionData?.personId || store.getters.stSessionData?.personid;
  return sessionPersonId ? Number(sessionPersonId) : null;
};

const personId = getPersonId();
const locationId = getLocationId();

const availableActivities = ref([]);
const userActivities = ref([]);
const availableWorkgroups = ref([]);
const userWorkgroups = ref([]);

// Out-of-scope IDs that the admin cannot see but must preserve on save
const outOfScopeActivityIds = ref([]);
const outOfScopeWorkgroupIds = ref([]);

// Permission-filtered available lists
const filteredAvailableActivities = computed(() => {
  if (isLocationAdmin.value) return availableActivities.value;
  if (adminActivityIds.value.length === 0) return availableActivities.value;
  const ids = new Set(adminActivityIds.value.map(Number));
  return availableActivities.value.filter((a) => ids.has(Number(a.id)));
});

const filteredAvailableWorkgroups = computed(() => {
  if (isLocationAdmin.value) return availableWorkgroups.value;
  if (adminWorkgroupIds.value.length === 0) return availableWorkgroups.value;
  const ids = new Set(adminWorkgroupIds.value.map(Number));
  return availableWorkgroups.value.filter((w) => ids.has(Number(w.id)));
});

// Map API data → TagSelector format
const mapActivities = (list) =>
  (list || []).map((a) => ({
    id: a.activityid,
    name: a.activityname,
    description: a.description ?? "",
  }));

const mapWorkgroups = (list) =>
  (list || []).map((w) => ({
    id: w.workgroupid,
    name: w.displayText,
    description: w.description ?? "",
  }));

// Fetch data
onMounted(async () => {
  loading.value = true;
  try {
    // Activities
    const activitiesRes = await store.dispatch("fetchPreferredActivities", {
      locationId,
      personId,
      peopleGroupId: getPeopleGroupId(),
    });
    availableActivities.value = mapActivities(activitiesRes.activities_list);
    const allUserActivities = mapActivities(activitiesRes.preferred_activities);

    // Workgroups
    const workgroupsRes = await store.dispatch("fetchPreferredWorkgroups", {
      locationId,
      personId,
      peopleGroupId: getPeopleGroupId(),
    });
    availableWorkgroups.value = mapWorkgroups(workgroupsRes.workgroup_list);
    const allUserWorkgroups = mapWorkgroups(workgroupsRes.preferred_work_groups);

    // If sub-location admin: split user prefs into in-scope (shown) and
    // out-of-scope (preserved silently on save).
    if (!isLocationAdmin.value && adminActivityIds.value.length > 0) {
      const actIds = new Set(adminActivityIds.value.map(Number));
      userActivities.value = allUserActivities.filter((a) => actIds.has(Number(a.id)));
      outOfScopeActivityIds.value = allUserActivities
        .filter((a) => !actIds.has(Number(a.id)))
        .map((a) => a.id);
    } else {
      userActivities.value = allUserActivities;
    }

    if (!isLocationAdmin.value && adminWorkgroupIds.value.length > 0) {
      const wgIds = new Set(adminWorkgroupIds.value.map(Number));
      userWorkgroups.value = allUserWorkgroups.filter((w) => wgIds.has(Number(w.id)));
      outOfScopeWorkgroupIds.value = allUserWorkgroups
        .filter((w) => !wgIds.has(Number(w.id)))
        .map((w) => w.id);
    } else {
      userWorkgroups.value = allUserWorkgroups;
    }
  } catch (err) {
    console.error("Failed to load user preferences:", err);
  } finally {
    loading.value = false;
  }
});

// Save handlers — merge out-of-scope IDs back in so we don't wipe data
// the admin cannot see.
const handleSaveActivities = async (selected) => {
  activitiesSubmitting.value = true;
  try {
    const selectedIds = selected.map((x) => x.id);
    const merged = [...selectedIds, ...outOfScopeActivityIds.value];
    await store.dispatch("savePreferredActivities", {
      personId,
      locationId,
      peopleGroupId: getPeopleGroupId(),
      activitiesArray: merged,
    });
    toast.success("Preferred activities saved.", { position: "top-right" });
  } catch (err) {
    console.error("Failed to save preferred activities:", err);
    toast.error(err?.response?.data?.error || err?.message || "Failed to save preferred activities.", { position: "top-right" });
  } finally {
    activitiesSubmitting.value = false;
  }
};

const handleSaveWorkgroups = async (selected) => {
  workgroupsSubmitting.value = true;
  try {
    const selectedIds = selected.map((x) => x.id);
    const merged = [...selectedIds, ...outOfScopeWorkgroupIds.value];
    await store.dispatch("savePreferredWorkgroups", {
      personId,
      locationId,
      peopleGroupId: getPeopleGroupId(),
      workgroupsArray: merged,
    });
    toast.success("Preferred workgroups saved.", { position: "top-right" });
  } catch (err) {
    console.error("Failed to save preferred workgroups:", err);
    toast.error(err?.response?.data?.error || err?.message || "Failed to save preferred workgroups.", { position: "top-right" });
  } finally {
    workgroupsSubmitting.value = false;
  }
};
</script>
